import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';

import { useMeasure } from 'react-use';
import s from './InsilicoBrowser.css';
import { getColorScale, svgToImage } from '../../../core/doc_util';
import Heatmap from '../../Heatmap';
import D3Legend from '../../Legend/D3Legend';
import { ANALYSES_META_INFO } from '../../../settings';

const ROW_NAMES = { A: 0, G: 1, C: 2, T: 3 };

export const InsilicoBrowser = () => {
  const [divRef, { width: containerWidth }] = useMeasure();
  const insilico = useSelector(state => state.deepsea.in_silico_mutagenesis);
  const [imageData, setImageData] = useState();

  useEffect(() => {
    setImageData(svgToImage('insilico'));
  }, [insilico, containerWidth]);

  if (insilico.selected === undefined) return null;

  const { featureName, sequenceName } = insilico.selected;
  const insilicoPositions = insilico[sequenceName][featureName].positions;
  const percentiles = insilico[sequenceName][featureName].percentile;

  const cellData = [];
  const rowNames = Object.keys(ROW_NAMES).map(rowName => ({ name: rowName }));
  const columnNames = [];
  let curPos = -1;
  let curColumn = -1;
  insilicoPositions.forEach(line => {
    const [pos, ref, alt, value] = line.split(',');
    if (ref !== 'N') {
      if (Number(curPos) !== Number(pos)) {
        // Only happens the 1st time 'pos' is seen, fills the blank cell
        curPos = pos;
        curColumn += 1;

        cellData.push({
          value: percentiles[50],
          row: ROW_NAMES[ref],
          column: curColumn,
          blank: true,
        });
        columnNames.push({ name: ref });
      }

      cellData.push({
        value: Number(value),
        row: ROW_NAMES[alt],
        column: curColumn,
      });
    }
  });

  const range = [percentiles[1], percentiles[50], percentiles[99]];
  const color = getColorScale(range, ['#2C72B7', '#FFFFFF', '#FFFD54']);
  const yAxisWidth = 12;
  const containerMargin = 5;

  const heatmapTooltip = cell =>
    cell.blank ? (
      <div>N / A</div>
    ) : (
      <div>{Number.parseFloat(cell.value).toPrecision(5)}</div>
    );

  return (
    <div ref={divRef}>
      <D3Legend
        width={containerWidth * 0.2}
        height={10}
        colorScale={color}
        range={range}
        title="Log-fold change"
        url={`${ANALYSES_META_INFO.ism?.helpUrl}#output`}
        tooltipText="Log-fold change score documentation"
      />
      <div className={s.heatmap}>
        <Heatmap
          cellHeight={62}
          cellWidth={11}
          columnValues={columnNames}
          columnHeaderRotation={0}
          rowValues={rowNames}
          style={{ textAnchor: 'middle' }}
          svgLayout={{
            margins: { top: 15, right: 0, bottom: 12, left: 0 },
            yAxisWidth,
            rowBuffer: 5,
            cellBorderSize: 0,
            addColumnCountAxis: true,
            xAxisTextOffset: { x: -0.35, y: 0.7 },
            tooltipOffset: { x: 20, y: 20 },
          }}
          tooltips={heatmapTooltip}
          heatmapInfo={{
            targetXPos: `1-${Math.round(columnNames.length / 2)}`,
            heatmapId: 'insilico',
          }}
          heatmapValues={cellData}
          color={color}
          maxBodyWidth={containerWidth - yAxisWidth - containerMargin}
        />
        {imageData ? (
          <img
            style={{ float: 'right' }}
            width={containerWidth - yAxisWidth}
            height={50}
            src={imageData}
            alt="Mini SVG map"
          />
        ) : (
          ''
        )}
      </div>
    </div>
  );
};

export default withStyles(s)(InsilicoBrowser);
